/**
 * Jubly SalesDrive — Email Campaigns
 * Campaign list + step editor (subject, body, delay) + enrollment controls
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Mail, Plus, X, Loader2, ChevronRight, ArrowLeft,
  Play, Pause, Archive, Trash2, Users, CheckCircle,
  Clock, Edit2, Save, AlertCircle, Send, GripVertical
} from 'lucide-react';

// ─── Helpers ──────────────────────────────────────────────────────────────────────────────

const STATUS_STYLES = {
  draft:    { label: 'Draft',    bg: 'bg-gray-100',   text: 'text-gray-600' },
  active:   { label: 'Active',   bg: 'bg-green-100',  text: 'text-green-700' },
  paused:   { label: 'Paused',   bg: 'bg-amber-100',  text: 'text-amber-700' },
  archived: { label: 'Archived', bg: 'bg-red-50',     text: 'text-red-500' },
};

function StatusBadge({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.draft;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${s.bg} ${s.text}`}>
      {s.label}
    </span>
  );
}

function delayLabel(days, hours) {
  if (!days && !hours) return 'Immediately';
  const parts = [];
  if (days)  parts.push(`${days}d`);
  if (hours) parts.push(`${hours}h`);
  return `After ${parts.join(' ')}`;
}

// ─── New Campaign Modal ───────────────────────────────────────────────────────────────

function NewCampaignModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: '', description: '', fromName: '', fromEmail: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name.trim()) { setError('Name is required'); return; }
    setSaving(true);
    setError(null);
    try {
      const res = await fetch('/api/crm/campaigns', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Failed to create');
      const campaign = await res.json();
      onCreated(campaign);
      onClose();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">New Campaign</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Campaign Name *</label>
            <input
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              value={form.name} onChange={e => set('name', e.target.value)}
              placeholder="e.g. Welcome Sequence" autoFocus
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
            <textarea rows={2}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
              value={form.description} onChange={e => set('description', e.target.value)}
              placeholder="What is this campaign for?"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">From Name</label>
              <input
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                value={form.fromName} onChange={e => set('fromName', e.target.value)}
                placeholder="Jane Smith"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">From Email</label>
              <input type="email"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                value={form.fromEmail} onChange={e => set('fromEmail', e.target.value)}
                placeholder="jane@example.com"
              />
            </div>
          </div>
          {error && <p className="text-xs text-red-500">{error}</p>}
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-60 flex items-center gap-2">
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Create Campaign
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Enroll Modal ────────────────────────────────────────────────────────────────────────────

function EnrollModal({ campaignId, onClose, onEnrolled }) {
  const [contacts, setContacts] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);

  useEffect(() => {
    fetch('/api/crm/contacts?limit=200', { credentials: 'include' })
      .then(r => r.json())
      .then(d => { setContacts(d.contacts || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const filtered = contacts.filter(c => {
    const q = search.toLowerCase();
    return (
      (c.first_name || '').toLowerCase().includes(q) ||
      (c.last_name  || '').toLowerCase().includes(q) ||
      (c.email      || '').toLowerCase().includes(q)
    );
  });

  function toggleContact(id) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function handleEnroll() {
    if (selected.size === 0) return;
    setEnrolling(true);
    try {
      const res = await fetch(`/api/crm/campaigns/${campaignId}/enroll`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contactIds: [...selected] }),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Failed');
      onEnrolled();
      onClose();
    } catch (err) {
      alert(err.message);
    } finally {
      setEnrolling(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">Enroll Contacts</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-4 py-3 border-b border-gray-100">
          <input
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder="Search contacts…"
            value={search} onChange={e => setSearch(e.target.value)}
          />
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-24">
              <Loader2 className="w-5 h-5 animate-spin text-indigo-400" />
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-8">No contacts found</p>
          ) : (
            <ul className="divide-y divide-gray-50">
              {filtered.map(c => (
                <li
                  key={c.id}
                  onClick={() => toggleContact(c.id)}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer"
                >
                  <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                    selected.has(c.id) ? 'bg-indigo-600 border-indigo-600' : 'border-gray-300'
                  }`}>
                    {selected.has(c.id) && (
                      <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">
                      {[c.first_name, c.last_name].filter(Boolean).join(' ') || '(No name)'}
                    </p>
                    {c.email && <p className="text-xs text-gray-400 truncate">{c.email}</p>}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between">
          <span className="text-xs text-gray-500">{selected.size} selected</span>
          <div className="flex gap-2">
            <button onClick={onClose}
              className="px-3 py-1.5 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
            <button onClick={handleEnroll} disabled={enrolling || selected.size === 0}
              className="px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-60 flex items-center gap-1.5">
              {enrolling && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Enroll {selected.size > 0 ? selected.size : ''}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Step Editor ─────────────────────────────────────────────────────────────────────────────

function StepEditor({ step, index, onChange, onRemove }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Step header */}
      <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 border-b border-gray-200">
        <GripVertical className="w-4 h-4 text-gray-300 flex-shrink-0" />
        <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold flex items-center justify-center flex-shrink-0">
          {index + 1}
        </div>
        <span className="text-sm font-medium text-gray-700 flex-1">Email Step {index + 1}</span>
        <div className="flex items-center gap-2">
          <Clock className="w-3.5 h-3.5 text-gray-400" />
          <span className="text-xs text-gray-500">{delayLabel(step.delayDays, step.delayHours)}</span>
        </div>
        <button onClick={() => onRemove(index)}
          className="text-gray-300 hover:text-red-500 transition-colors">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Step body */}
      <div className="p-4 space-y-3">
        {/* Delay */}
        <div className="flex items-center gap-3">
          <span className="text-xs font-medium text-gray-500 w-16 flex-shrink-0">Send after</span>
          <div className="flex items-center gap-2">
            <input
              type="number" min="0" max="365"
              className="w-16 border border-gray-200 rounded-lg px-2 py-1.5 text-sm text-center focus:outline-none focus:ring-2 focus:ring-indigo-500"
              value={step.delayDays}
              onChange={e => onChange(index, 'delayDays', parseInt(e.target.value, 10) || 0)}
            />
            <span className="text-xs text-gray-500">days</span>
            <input
              type="number" min="0" max="23"
              className="w-16 border border-gray-200 rounded-lg px-2 py-1.5 text-sm text-center focus:outline-none focus:ring-2 focus:ring-indigo-500"
              value={step.delayHours}
              onChange={e => onChange(index, 'delayHours', parseInt(e.target.value, 10) || 0)}
            />
            <span className="text-xs text-gray-500">hours</span>
          </div>
        </div>

        {/* Subject */}
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Subject line</label>
          <input
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            value={step.subject}
            onChange={e => onChange(index, 'subject', e.target.value)}
            placeholder="e.g. Welcome to our community!"
          />
        </div>

        {/* Body */}
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Email body</label>
          <textarea
            rows={5}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-y font-mono"
            value={step.body}
            onChange={e => onChange(index, 'body', e.target.value)}
            placeholder="Hi {{first_name}},\n\nWelcome aboard! We’re excited to have you.…"
          />
          <p className="text-[11px] text-gray-400 mt-1">Use {'{{first_name}}'}, {'{{last_name}}'}, {'{{email}}'} for personalization</p>
        </div>
      </div>
    </div>
  );
}

// ─── Campaign Detail View ───────────────────────────────────────────────────────────────

function CampaignDetail({ campaignId, onBack, onUpdated }) {
  const [campaign, setCampaign] = useState(null);
  const [steps, setSteps] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [tab, setTab] = useState('steps');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showEnroll, setShowEnroll] = useState(false);

  async function loadCampaign() {
    setLoading(true);
    try {
      const res = await fetch(`/api/crm/campaigns/${campaignId}`, { credentials: 'include' });
      const data = await res.json();
      setCampaign(data);
      setSteps((data.steps || []).map(s => ({
        subject:    s.subject    || '',
        body:       s.body       || '',
        delayDays:  s.delay_days  || 0,
        delayHours: s.delay_hours || 0,
      })));
    } catch {}
    setLoading(false);
  }

  async function loadEnrollments() {
    try {
      const res = await fetch(`/api/crm/campaigns/${campaignId}/enrollments`, { credentials: 'include' });
      const data = await res.json();
      setEnrollments(data.enrollments || []);
    } catch {}
  }

  useEffect(() => { loadCampaign(); }, [campaignId]);
  useEffect(() => { if (tab === 'enrollments') loadEnrollments(); }, [tab]);

  function handleStepChange(index, field, value) {
    setSteps(prev => prev.map((s, i) => i === index ? { ...s, [field]: value } : s));
  }

  function addStep() {
    setSteps(prev => [...prev, { subject: '', body: '', delayDays: 1, delayHours: 0 }]);
  }

  function removeStep(index) {
    setSteps(prev => prev.filter((_, i) => i !== index));
  }

  async function saveSteps() {
    setSaving(true);
    try {
      const res = await fetch(`/api/crm/campaigns/${campaignId}/steps`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ steps }),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Failed');
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function updateStatus(status) {
    try {
      const res = await fetch(`/api/crm/campaigns/${campaignId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Failed');
      setCampaign(prev => ({ ...prev, status }));
      onUpdated();
    } catch (err) {
      alert(err.message);
    }
  }

  async function handleUnenroll(contactId) {
    if (!window.confirm('Remove this contact from the campaign?')) return;
    try {
      await fetch(`/api/crm/campaigns/${campaignId}/enrollments/${contactId}`, {
        method: 'DELETE', credentials: 'include',
      });
      loadEnrollments();
    } catch {}
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-gray-400">
        <p className="text-sm">Campaign not found</p>
        <button onClick={onBack} className="mt-3 text-sm text-indigo-600 hover:underline">← Back</button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 px-6 py-4 flex-shrink-0">
        <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 mb-3">
          <ArrowLeft className="w-4 h-4" /> Back to Campaigns
        </button>
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-lg font-bold text-gray-900">{campaign.name}</h1>
              <StatusBadge status={campaign.status} />
            </div>
            {campaign.description && (
              <p className="text-sm text-gray-500 mt-0.5">{campaign.description}</p>
            )}
            <div className="flex items-center gap-4 mt-1.5 text-xs text-gray-400">
              {campaign.from_name  && <span>From: {campaign.from_name}</span>}
              {campaign.from_email && <span>{campaign.from_email}</span>}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {campaign.status === 'draft' && (
              <button onClick={() => updateStatus('active')}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700">
                <Play className="w-3.5 h-3.5" /> Activate
              </button>
            )}
            {campaign.status === 'active' && (
              <button onClick={() => updateStatus('paused')}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-amber-700 bg-amber-100 rounded-lg hover:bg-amber-200">
                <Pause className="w-3.5 h-3.5" /> Pause
              </button>
            )}
            {campaign.status === 'paused' && (
              <button onClick={() => updateStatus('active')}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700">
                <Play className="w-3.5 h-3.5" /> Resume
              </button>
            )}
            <button onClick={() => setShowEnroll(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-indigo-700 bg-indigo-50 rounded-lg hover:bg-indigo-100">
              <Users className="w-3.5 h-3.5" /> Enroll Contacts
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 px-6 py-2 bg-white border-b border-gray-100 flex-shrink-0">
        {['steps', 'enrollments'].map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-1.5 text-sm font-medium rounded-lg transition-colors ${
              tab === t ? 'bg-indigo-50 text-indigo-700' : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50'
            }`}>
            {t === 'steps' ? 'Email Steps' : 'Enrollments'}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto bg-gray-50/40">
        {tab === 'steps' ? (
          <div className="max-w-2xl mx-auto px-6 py-6 space-y-4">
            {steps.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                <Mail className="w-10 h-10 mb-3 stroke-1" />
                <p className="text-sm font-medium">No steps yet</p>
                <p className="text-xs mt-1">Add email steps to build your sequence</p>
              </div>
            ) : (
              steps.map((step, i) => (
                <StepEditor
                  key={i}
                  step={step}
                  index={i}
                  onChange={handleStepChange}
                  onRemove={removeStep}
                />
              ))
            )}

            {/* Add step + Save */}
            <div className="flex items-center gap-3 pt-2">
              <button onClick={addStep}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-50 transition-colors">
                <Plus className="w-4 h-4" /> Add Step
              </button>
              {steps.length > 0 && (
                <button onClick={saveSteps} disabled={saving}
                  className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-60 transition-colors">
                  {saving ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : saved ? (
                    <CheckCircle className="w-3.5 h-3.5" />
                  ) : (
                    <Save className="w-3.5 h-3.5" />
                  )}
                  {saved ? 'Saved!' : 'Save Steps'}
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="max-w-2xl mx-auto px-6 py-6">
            {enrollments.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                <Users className="w-10 h-10 mb-3 stroke-1" />
                <p className="text-sm font-medium">No enrollments yet</p>
                <button onClick={() => setShowEnroll(true)}
                  className="mt-3 flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-50">
                  <Plus className="w-3.5 h-3.5" /> Enroll Contacts
                </button>
              </div>
            ) : (
              <ul className="space-y-2">
                {enrollments.map(e => (
                  <li key={e.id} className="bg-white rounded-xl border border-gray-100 px-4 py-3 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 text-sm font-semibold flex items-center justify-center flex-shrink-0">
                      {(e.contact_name || e.contact_email || '?')[0].toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{e.contact_name || '(No name)'}</p>
                      {e.contact_email && <p className="text-xs text-gray-400 truncate">{e.contact_email}</p>}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        e.status === 'active'       ? 'bg-green-100 text-green-700' :
                        e.status === 'completed'    ? 'bg-blue-100 text-blue-700' :
                        e.status === 'unsubscribed' ? 'bg-gray-100 text-gray-500' :
                        'bg-red-100 text-red-600'
                      }`}>{e.status}</span>
                      <span className="text-xs text-gray-400">Step {e.current_step + 1}</span>
                      <button onClick={() => handleUnenroll(e.contact_id)}
                        className="text-gray-300 hover:text-red-500 transition-colors">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {showEnroll && (
        <EnrollModal
          campaignId={campaignId}
          onClose={() => setShowEnroll(false)}
          onEnrolled={() => { loadEnrollments(); setTab('enrollments'); }}
        />
      )}
    </div>
  );
}

// ─── Campaign List ────────────────────────────────────────────────────────────────────────────

function CampaignList({ onSelect }) {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');

  const fetchCampaigns = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ limit: 50 });
    if (statusFilter) params.set('status', statusFilter);
    try {
      const res = await fetch(`/api/crm/campaigns?${params}`, { credentials: 'include' });
      const data = await res.json();
      setCampaigns(data.campaigns || []);
    } catch {}
    setLoading(false);
  }, [statusFilter]);

  useEffect(() => { fetchCampaigns(); }, [fetchCampaigns]);

  async function handleDelete(id, e) {
    e.stopPropagation();
    if (!window.confirm('Delete this campaign? This cannot be undone.')) return;
    await fetch(`/api/crm/campaigns/${id}`, { method: 'DELETE', credentials: 'include' });
    fetchCampaigns();
  }

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100 bg-white">
        <h1 className="text-base font-semibold text-gray-900 mr-2">Email Campaigns</h1>
        <select
          className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
          value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
        >
          <option value="">All Statuses</option>
          <option value="draft">Draft</option>
          <option value="active">Active</option>
          <option value="paused">Paused</option>
          <option value="archived">Archived</option>
        </select>
        <button onClick={() => setShowNew(true)}
          className="ml-auto flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors">
          <Plus className="w-4 h-4" /> New Campaign
        </button>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto bg-gray-50/40">
        {loading ? (
          <div className="flex items-center justify-center h-40">
            <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
          </div>
        ) : campaigns.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-40 text-gray-400">
            <Send className="w-8 h-8 mb-2 stroke-1" />
            <p className="text-sm">No campaigns yet</p>
            <button onClick={() => setShowNew(true)}
              className="mt-3 flex items-center gap-1.5 text-sm text-indigo-600 hover:underline">
              <Plus className="w-3.5 h-3.5" /> Create your first campaign
            </button>
          </div>
        ) : (
          <ul className="divide-y divide-gray-100 bg-white">
            {campaigns.map(c => (
              <li
                key={c.id}
                onClick={() => onSelect(c.id)}
                className="flex items-center gap-4 px-6 py-4 hover:bg-gray-50 cursor-pointer transition-colors group"
              >
                <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-indigo-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-gray-900 truncate">{c.name}</p>
                    <StatusBadge status={c.status} />
                  </div>
                  <div className="flex items-center gap-4 mt-0.5">
                    <span className="text-xs text-gray-400">
                      {c.step_count || 0} step{c.step_count !== 1 ? 's' : ''}
                    </span>
                    <span className="text-xs text-gray-400">
                      {c.active_enrollments || 0} active · {c.total_enrollments || 0} total enrolled
                    </span>
                    {c.description && (
                      <span className="text-xs text-gray-400 truncate hidden sm:block">{c.description}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={e => handleDelete(c.id, e)}
                    className="p-1.5 text-gray-300 hover:text-red-500 rounded-lg hover:bg-red-50 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  <ChevronRight className="w-4 h-4 text-gray-300" />
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {showNew && (
        <NewCampaignModal
          onClose={() => setShowNew(false)}
          onCreated={campaign => {
            fetchCampaigns();
            onSelect(campaign.id);
          }}
        />
      )}
    </div>
  );
}

// ─── Main Export ──────────────────────────────────────────────────────────────────────────────

export default function SalesCampaigns() {
  const [selectedId, setSelectedId] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {selectedId ? (
        <CampaignDetail
          campaignId={selectedId}
          onBack={() => setSelectedId(null)}
          onUpdated={() => setRefreshKey(k => k + 1)}
        />
      ) : (
        <CampaignList key={refreshKey} onSelect={setSelectedId} />
      )}
    </div>
  );
}
